// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Selectable.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import { omit, uniqueId, find, findIndex } from 'lodash/fp';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';

class SelectableState {
  @observable selected = false;

  /**
   * Initial instant
   * @param {boolean} selected
   */
  constructor(selected: boolean) {
    this.selected = selected;
  }
}

export class SelectableContext {
  constructor() {
    this.observers = [];
  }

  /**
   * Register a selectable component
   * @param {string} key
   * @param {SelectableState} selectableState
   */
  register(key: string, selectableState: SelectableState) {
    this.observers.push({ key, selectableState });
  }

  /**
   * Unregister a selectable component
   * @param {string} key
   */
  unregister(key: string) {
    const index = findIndex(i => i.key === key, this.observers);
    index > -1 && this.observers.splice(index, 1);
  }

  /**
   * Handle select a selectable component
   * @param {string} key
   */
  @action
  onSelect(key: string) {
    const selectedItem = find(item => item.key === key, this.observers);
    if (selectedItem.selectableState.selected) return false;

    this.observers.forEach(item => {
      item.selectableState.selected && (item.selectableState.selected = false);
    });

    selectedItem.selectableState.selected = true;
    return true;
  }
}

/**
 * HOC makes a selectable component
 * Component mush have selected props and onClick props
 * @param {Component} WrappedComponent
 * @param {SelectableContext} context
 */
export const createSelectable = (WrappedComponent: Component, context: SelectableContext) => {
  @observer
  class SelectableItem extends Component {
    constructor(props) {
      super(props);
      this.selectableState = new SelectableState(!!props.selected);
      this.key = uniqueId('Selectable_');
    }
    componentDidMount() {
      context.register(this.key, this.selectableState);
    }
    componentWillUnmount() {
      context.unregister(this.key);
    }
    onClickHandler = (e: any) => {
      if (context.onSelect(this.key)) {
        this.props.onClick && this.props.onClick(e);
      }
    };

    render() {
      const restProps = omit(['selected'], this.props);
      return (
        <div onClick={this.onClickHandler}>
          <WrappedComponent {...restProps} selected={this.selectableState.selected} />
        </div>
      );
    }
  }

  return SelectableItem;
};



// WEBPACK FOOTER //
// ./ui/share/components/hoc/Izenda.Selectable.js